import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import {
  PAIR_AMOUNT_DECIMALS,
  USD_DECIMALS,
} from '@ui/constants/numbersDecimals';
import {
  calculateSpreadPercentage,
  useSpreadPercentage,
} from '@ui/features/Spread/useSpreadPercentage';
import { RequireConnectionClickable } from '@ui/features/Wallet/components/RequireConnectionClickable';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { useTokenSizeDisplayDecimals } from '@ui/hooks/useTokenSizeDisplayDecimals';
import { toBigValue, toReadableValue } from '@ui/lib/bignumber';
import { Button } from '@ui/lib/park/ui/button';
import { Text } from '@ui/lib/park/ui/text';
import { useBatchUpdateDecreaseOrders } from '@ui/services/contract/hooks/TradingPortalFacet/useBatchUpdateDecreaseOrders/useBatchUpdateDecreaseOrders';
import type { DecreaseOrderRow } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import BigNumber from 'bignumber.js';
import { useMemo, useState } from 'react';
import { Box, Grid, HStack, Stack } from 'styled-system/jsx';

import { useGetTargetMaxTpRatios } from '../../hooks/useGetTargetMaxTpRatios';
import type { GenericTradeParams, TpSlValueState } from '../../types';
import { CloseQtyPercentageSelector } from '../ClosePosition/CloseQtyPercentageSelector';
import { formatLeverage } from '../TradeModule/utils/formatLeverage';
import { EditTpSl } from '../EditTpSl/components/EditTpSl';
import {
  useEditSlValidation,
  useEditTpValidation,
} from '../EditTpSl/hooks/useEditTpSlValidation';
import { valueStyle } from '../EditTpSl/styles';

const getCloseQtyPercent = ({
  closeQty,
  positionSize,
}: {
  closeQty: BigNumber;
  positionSize: BigNumber;
}) => {
  if (positionSize.isZero()) return 100;

  return closeQty.div(positionSize).times(100).decimalPlaces(0).toNumber();
};

export const EditDecreaseOrderForm = ({
  order,
  genericTradeParams,
  collateralMarketPrice,
  pairMarketPrice,
  onDone,
}: {
  order: DecreaseOrderRow;
  genericTradeParams: GenericTradeParams['genericTradeParams'];
  collateralMarketPrice: BigNumber;
  pairMarketPrice: BigNumber;
  onDone: () => void;
}) => {
  const initialTriggerPrice = toReadableValue(order.triggerPrice, USD_DECIMALS);
  const initialCloseQty = toReadableValue(order.closeQty, PAIR_AMOUNT_DECIMALS);
  const priceDisplayDecimals = useTokenDisplayDecimals(
    genericTradeParams.pair.baseSymbol,
    genericTradeParams.pair.priceDisplayDecimals,
  );
  const [tp, setTp] = useState<TpSlValueState>(() => ({
    priceValue:
      order.kind === 'TP'
        ? initialTriggerPrice.toFixed(priceDisplayDecimals)
        : '',
    pnlPercentage: undefined,
    deltaPnl: undefined,
    isLong: genericTradeParams.isLong,
    isPnlPercentage: false,
  }));
  const [sl, setSl] = useState<TpSlValueState>(() => ({
    priceValue:
      order.kind === 'SL'
        ? initialTriggerPrice.toFixed(priceDisplayDecimals)
        : '',
    pnlPercentage: undefined,
    deltaPnl: undefined,
    isLong: genericTradeParams.isLong,
    isPnlPercentage: false,
  }));
  const [closeQtyPercent, setCloseQtyPercent] = useState(() =>
    getCloseQtyPercent({
      closeQty: initialCloseQty,
      positionSize: genericTradeParams.size,
    }),
  );
  const { batchUpdate, isPending } = useBatchUpdateDecreaseOrders({
    onSuccess: onDone,
  });
  const tradePair = useTradePairInfosByPairBase(
    genericTradeParams.pair.address,
  );
  const positionSizeUsd = genericTradeParams.getSizeUsd(pairMarketPrice);
  const spreadPercentageInfo = useSpreadPercentage({
    tradePair,
    positionSize: positionSizeUsd,
    pairPrice: pairMarketPrice,
  });
  const spreadPercentage = calculateSpreadPercentage({
    isLong: genericTradeParams.isLong,
    isClosePrice: true,
    spreadPercentageInfo,
  });
  const leverageValue = genericTradeParams
    .getLeverage({
      collateralTokenMarketPrice: collateralMarketPrice,
    })
    .toNumber();
  const maxTpRatios = useGetTargetMaxTpRatios(
    genericTradeParams.pair.address,
    leverageValue,
  );
  const collateralUsdBeforeOpenPositionFees =
    genericTradeParams.getCollateralUsdBeforeOpenPositionFees(
      collateralMarketPrice,
    );
  const tpValidation = useEditTpValidation({
    genericTradeParams,
    tp,
    maxTpRatios,
    spreadPercentage,
    leverage: leverageValue,
  });
  const slValidation = useEditSlValidation({
    genericTradeParams,
    sl,
  });
  const selectedCloseQty = useMemo(() => {
    return genericTradeParams.size.multipliedBy(closeQtyPercent).div(100);
  }, [closeQtyPercent, genericTradeParams.size]);
  const livePairMarketPrice = usePriceLive(genericTradeParams.pair.baseSymbol);
  const selectedCloseQtyUsd = selectedCloseQty.multipliedBy(
    livePairMarketPrice ?? pairMarketPrice,
  );
  const sizeDisplayDecimals = useTokenSizeDisplayDecimals(
    genericTradeParams.pair.baseSymbol,
  );
  const nextTriggerPrice = new BigNumber(
    order.kind === 'TP' ? tp.priceValue : sl.priceValue,
  );
  const nextCloseQty = toBigValue(selectedCloseQty, PAIR_AMOUNT_DECIMALS);
  const isUnchanged =
    nextTriggerPrice.isEqualTo(initialTriggerPrice) &&
    nextCloseQty === order.closeQty &&
    closeQtyPercent ===
      getCloseQtyPercent({
        closeQty: initialCloseQty,
        positionSize: genericTradeParams.size,
      });
  const isDisabled =
    isUnchanged ||
    !nextTriggerPrice.isFinite() ||
    nextTriggerPrice.lte(0) ||
    (order.kind === 'TP' && tpValidation?.valid === false) ||
    (order.kind === 'SL' && slValidation?.valid === false);

  return (
    <Stack gap="2">
      <Text textStyle="v2.meta" color="v2.text.muted">
        Update trigger price and close qty for this {order.kind} order.
      </Text>
      <Box py="1">
        <EditTpSl
          tp={tp}
          setTp={setTp}
          sl={sl}
          setSl={setSl}
          genericTradeParams={genericTradeParams}
          isPositionType
          maxTpRatios={maxTpRatios}
          spreadPercentage={spreadPercentage}
          leverage={leverageValue}
          collateralUsd={collateralUsdBeforeOpenPositionFees}
          tpValidation={order.kind === 'TP' ? tpValidation : undefined}
          slValidation={order.kind === 'SL' ? slValidation : undefined}
        />
      </Box>
      <CloseQtyPercentageSelector
        value={closeQtyPercent}
        onChange={setCloseQtyPercent}
        mt="0"
        flush
      />
      <Grid
        gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr) 136px"
        alignItems="center"
        gap="2"
        pt="3"
        borderTop="1px solid"
        borderTopColor="v2.border.hairline"
      >
        <SummaryStat label="Close qty">
          <HStack gap="1" minW={0}>
            <Amount
              value={selectedCloseQty}
              css={valueStyle}
              textAlign="start"
              opts={{
                displayDecimals: sizeDisplayDecimals,
                suffix: ` ${genericTradeParams.pair.baseSymbol}`,
              }}
            />
            <AmountUsd
              value={selectedCloseQtyUsd}
              textStyle="numbers.xsmall"
              color="v2.text.subtle"
              opts={{
                prefix: '($',
                suffix: ')',
              }}
            />
          </HStack>
        </SummaryStat>
        <SummaryStat label="Leverage" align="end">
          {`${formatLeverage(leverageValue)}x`}
        </SummaryStat>
        <RequireConnectionClickable>
          <Button
            variant="primary"
            w="full"
            h="40px"
            loading={isPending}
            disabled={isDisabled}
            onClick={() => {
              batchUpdate([
                {
                  orderHash: order.orderHash,
                  triggerPrice: toBigValue(nextTriggerPrice, USD_DECIMALS),
                  closeQty: nextCloseQty,
                  broker: order.broker,
                },
              ]);
            }}
          >
            Update Order
          </Button>
        </RequireConnectionClickable>
      </Grid>
    </Stack>
  );
};

export const SummaryStat = ({
  label,
  children,
  align = 'start',
}: {
  label: string;
  children: React.ReactNode;
  align?: 'start' | 'end';
}) => (
  <Stack
    gap="0"
    minW={0}
    alignItems={align === 'end' ? 'flex-end' : 'flex-start'}
  >
    <Text textStyle="v2.meta" color="v2.text.muted">
      {label}
    </Text>
    <Box css={valueStyle} textAlign={align} truncate>
      {children}
    </Box>
  </Stack>
);
